import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Heart, Eye, MousePointerClick, ShoppingCart, FileText, Zap, PieChart as PieIcon,
  BarChart3, Target, TrendingUp, Lightbulb, Download, Sparkles, Loader2, CalendarDays, Image as ImageIcon, Users, Link2, RefreshCw,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from "recharts";
import api, { apiError } from "@/lib/api";
import { SafeChart } from "@/components/SafeChart";
import { toast } from "sonner";
import {
  PASTEL, NET_COLORS, NET_LABELS, SERVICE_LABELS, fmtN, fmtPct, initials,
  AnCard, AnKpi, Donut, TopPubs, ObjectifsBars, Spark, NetIcon, INSIGHT_ICONS,
} from "./AnalyseBlocks";
import { AnalyseRapports } from "./AnalyseRapports";

const API = process.env.REACT_APP_BACKEND_URL;
const SUB_TABS = [
  ["overview", "Vue d'ensemble"], ["contenus", "Contenus"], ["audiences", "Audiences"],
  ["performances", "Performances"], ["objectifs", "Objectifs"], ["insights", "Insights"],
  ["rapports", "Rapports"],
];
const PERF_METRICS = [["reach", "Portée", "#E1306C"], ["impressions", "Impressions", "#0369A1"],
  ["clicks", "Clics lien", "#6B21A8"], ["conversions", "Conversions", "#15803D"]];

function PerfChart({ series, testid, height = 190 }) {
  const [hidden, setHidden] = useState(["conversions"]);
  const data = useMemo(() => {
    if (series.length <= 45) return series;
    const step = Math.ceil(series.length / 45);
    return series.filter((_, i) => i % step === 0);
  }, [series]);
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 px-4 pt-2">
        {PERF_METRICS.map(([id, l, color]) => (
          <button key={id} data-testid={`${testid}-toggle-${id}`}
            onClick={() => setHidden((h) => h.includes(id) ? h.filter((x) => x !== id) : [...h, id])}
            className={`flex items-center gap-1.5 text-[10px] font-medium rounded-full px-2 py-0.5 border transition-colors ${
              hidden.includes(id) ? "opacity-40 border-neutral-200 text-neutral-400" : "border-neutral-200 text-neutral-600"}`}>
            <span className="w-2 h-2 rounded-full" style={{ background: color }} /> {l}
          </button>
        ))}
      </div>
      <div className="px-2 pb-2 pt-1">
        <SafeChart height={height}>
          <LineChart data={data} margin={{ top: 8, right: 10, left: -14, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false}
              tickFormatter={(d) => { try { return format(parseISO(d), "d MMM", { locale: fr }); } catch { return d; } }} />
            <YAxis tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtN(v)} />
            <RTooltip formatter={(v, n) => [fmtN(v), (PERF_METRICS.find(([id]) => id === n) || [])[1] || n]}
              labelFormatter={(d) => { try { return format(parseISO(d), "d MMMM yyyy", { locale: fr }); } catch { return d; } }} />
            {PERF_METRICS.filter(([id]) => !hidden.includes(id)).map(([id, , color]) => (
              <Line key={id} type="monotone" dataKey={id} stroke={color} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        </SafeChart>
      </div>
    </div>
  );
}

function MonthlyTable({ rows, testid }) {
  if (!rows?.length) return <p className="px-4 py-6 text-[13px] text-neutral-400">Aucune donnée mensuelle.</p>;
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-[9px] font-semibold uppercase tracking-wider text-neutral-400 border-b border-neutral-100">
            {["Mois", "Abonnés", "Portée", "Impressions", "Clics lien", "Conversions", "Taux d'eng.", "Évolution"].map((h) => (
              <th key={h} className="px-4 py-2 text-left last:text-right">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month} className="border-b border-neutral-50 last:border-0" data-testid={`${testid}-${r.month}`}>
              <td className="px-4 py-2 font-medium text-[#0F172A] capitalize whitespace-nowrap">{r.label}</td>
              <td className="px-4 py-2 text-neutral-600">{fmtN(r.abonnes)}</td>
              <td className="px-4 py-2 text-neutral-600">{fmtN(r.portee)}</td>
              <td className="px-4 py-2 text-neutral-600">{fmtN(r.impressions)}</td>
              <td className="px-4 py-2 text-neutral-600">{fmtN(r.clics)}</td>
              <td className="px-4 py-2 text-neutral-600">{fmtN(r.conversions)}</td>
              <td className="px-4 py-2 text-neutral-600">{fmtPct(r.engagement)}</td>
              <td className={`px-4 py-2 text-right font-semibold ${r.delta == null ? "text-neutral-300" : r.delta >= 0 ? "text-[#15803D]" : "text-[#BE123C]"}`}>
                {r.delta != null ? `↗ ${r.delta >= 0 ? "+" : ""}${r.delta} %` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FollowersChart({ followers, testid, height = 170 }) {
  const nets = Object.keys(followers || {});
  const [net, setNet] = useState(nets[0] || "");
  const active = nets.includes(net) ? net : nets[0];
  const serie = (followers[active]?.series || []).map((d) => ({ ...d, followers: d.followers }));
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 px-4 pt-2">
        <p className="font-heading text-xl font-bold text-[#0F172A]">{fmtN(followers[active]?.total)}</p>
        {followers[active]?.delta != null && (
          <span className={`text-[11px] font-semibold ${followers[active].delta >= 0 ? "text-[#15803D]" : "text-[#BE123C]"}`}>
            ↗ {followers[active].delta >= 0 ? "+" : ""}{followers[active].delta} %</span>
        )}
        <select value={active} onChange={(e) => setNet(e.target.value)} data-testid={`${testid}-net`}
          className="ml-auto text-[11px] border border-[#E2E8F0] rounded-lg px-2 py-1 bg-white text-neutral-600">
          {nets.map((n) => <option key={n} value={n}>{NET_LABELS[n]}</option>)}
        </select>
      </div>
      <div className="px-2 pb-2 pt-1">
        <SafeChart height={height}>
          <LineChart data={serie} margin={{ top: 8, right: 10, left: -14, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false}
              tickFormatter={(d) => { try { return format(parseISO(d), "d MMM", { locale: fr }); } catch { return d; } }} />
            <YAxis tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} domain={["auto", "auto"]}
              tickFormatter={(v) => fmtN(v)} />
            <RTooltip formatter={(v) => [fmtN(v), "Abonnés"]}
              labelFormatter={(d) => { try { return format(parseISO(d), "d MMMM yyyy", { locale: fr }); } catch { return d; } }} />
            <Line type="monotone" dataKey="followers" stroke={NET_COLORS[active] || "#E1306C"} strokeWidth={2} dot={false} />
          </LineChart>
        </SafeChart>
      </div>
    </div>
  );
}

function NetworkTable({ rows, testid }) {
  if (!rows?.length) return <p className="px-4 py-6 text-[13px] text-neutral-400">Aucun réseau connecté.</p>;
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-3 px-4 pt-2 text-[9px] font-semibold uppercase tracking-wider text-neutral-400">
        <span>Réseau</span><span className="text-right">Abonnés</span><span className="text-right">Portée moy.</span>
        <span className="text-right">Engagement</span><span className="text-right">Clics lien</span>
      </div>
      {rows.map((n) => (
        <div key={n.id} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-3 items-center px-4 py-2.5 border-b border-neutral-100 last:border-0"
          data-testid={`${testid}-${n.id}`}>
          <span className="flex items-center gap-2 min-w-0">
            <NetIcon net={n.id} />
            <Spark data={n.spark} color={NET_COLORS[n.id]} w={48} h={18} />
          </span>
          <span className="text-right">
            <span className="block text-[12px] font-bold text-[#0F172A]">{fmtN(n.abonnes)}</span>
            {n.abonnes_delta != null && (
              <span className={`block text-[9px] font-semibold ${n.abonnes_delta >= 0 ? "text-[#15803D]" : "text-[#BE123C]"}`}>
                ↗ {n.abonnes_delta >= 0 ? "+" : ""}{n.abonnes_delta} %</span>
            )}
          </span>
          <span className="text-[11px] text-neutral-600 text-right">{fmtN(n.portee_moy)}</span>
          <span className="text-[11px] text-neutral-600 text-right">{fmtPct(n.engagement)}</span>
          <span className="text-[11px] text-neutral-600 text-right">{fmtN(n.clics)}</span>
        </div>
      ))}
    </div>
  );
}

function InsightsBlock({ insights, testid }) {
  if (!insights?.length) {
    return <p className="px-4 py-6 text-[13px] text-neutral-400">Pas assez de données sur la période pour dégager des enseignements fiables.</p>;
  }
  return (
    <div className="divide-y divide-neutral-100" data-testid={testid}>
      {insights.map((ins, i) => {
        const I = INSIGHT_ICONS[ins.icon] || Lightbulb;
        return (
          <div key={i} className="flex items-start gap-3 px-4 py-3" data-testid={`${testid}-${i}`}>
            <span className="w-8 h-8 rounded-xl border flex items-center justify-center shrink-0"
              style={{ background: PASTEL.green.bg, color: PASTEL.green.text, borderColor: PASTEL.green.border }}>
              <I className="w-4 h-4" strokeWidth={1.8} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-[#0F172A]">{ins.title}</p>
              <p className="text-[11px] text-neutral-500 leading-snug mt-0.5">{ins.text}</p>
            </div>
            {ins.action && (
              <span className="text-[9px] font-semibold rounded-full px-2 py-1 bg-green-50 text-green-700 border border-green-200 whitespace-nowrap shrink-0">
                {ins.action}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ClientObjectivesEditor({ clientId, objectives, onSaved }) {
  const [form, setForm] = useState(() => Object.fromEntries(
    ["portee", "engagement", "clics", "conversions", "abonnes"].map((k) => [k, ""])));
  const [saving, setSaving] = useState(false);
  useMemo(() => {
    setForm((f) => ({ ...f, ...Object.fromEntries((objectives || []).map((o) => [o.id, o.target || ""])) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectives]);
  const save = async () => {
    setSaving(true);
    try {
      const cm_objectives = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v) || 0]));
      await api.patch(`/clients/${clientId}`, { cm_objectives });
      toast.success("Objectifs du client enregistrés");
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="px-4 py-3 space-y-3" data-testid="client-objectifs-form">
      <div className="grid grid-cols-2 gap-3">
        {[["portee", "Portée totale"], ["engagement", "Taux d'engagement (%)"], ["clics", "Clics lien"],
          ["conversions", "Conversions"], ["abonnes", "Croissance abonnés"]].map(([k, l]) => (
          <label key={k} className="block">
            <span className="text-[11px] font-medium text-neutral-500">{l}</span>
            <input type="number" min="0" step={k === "engagement" ? "0.1" : "1"} value={form[k]}
              data-testid={`client-obj-${k}`} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              className="mt-0.5 w-full text-sm border border-[#E2E8F0] rounded-lg px-2.5 py-2" />
          </label>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="client-obj-save"
        className="text-xs font-semibold bg-[#0B132B] text-white rounded-lg px-4 py-2 hover:bg-[#1C2A4F] disabled:opacity-50">
        {saving ? "Enregistrement…" : "Enregistrer les objectifs"}
      </button>
    </div>
  );
}

/* ----------------------------- Vue client ----------------------------- */
export function AnalyseClientView({ data, period, subDefault, clients, onBack }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState(SUB_TABS.some(([id]) => id === subDefault) ? subDefault : "overview");
  const [exporting, setExporting] = useState(false);
  const c = data.client;
  const connected = data.connected !== false;
  const [syncing, setSyncing] = useState(false);
  // La connexion Meta du client vit sur la fiche client (onglet Community
  // Management → Connexion Meta) — même source de vérité que le moteur d'analyse.
  const connectMeta = () => navigate(`/clients/${c.id}?tab=apercu`);
  const syncMeta = async () => {
    setSyncing(true);
    try {
      await api.post("/meta/sync", { client_id: c.id });
      toast.success("Statistiques synchronisées depuis Meta");
      window.location.reload();
    } catch (e) { toast.error(apiError(e)); setSyncing(false); }
  };
  const tags = [...(c.sector ? [c.sector] : []),
    ...(c.services || []).map((s) => SERVICE_LABELS[s]).filter(Boolean), "B2C"];
  const kpis = [
    ...Object.entries(data.followers || {}).map(([net, f], i) => ({
      id: `abonnes-${net}`, label: `Abonnés ${NET_LABELS[net]}`, value: fmtN(f.total),
      delta: f.delta, icon: i % 2 ? Users : Eye,
      pastel: [PASTEL.rose, PASTEL.blue, PASTEL.purple, PASTEL.amber][i % 4],
    })),
    { id: "engagement", label: "Taux d'engagement", value: fmtPct(data.engagement),
      delta: data.prev_engagement ? Math.round((data.engagement - data.prev_engagement) * 10) / 10 : null,
      icon: Heart, pastel: PASTEL.rose, isPt: true },
    { id: "portee-moy", label: "Portée moyenne", value: fmtN(data.avg_reach),
      delta: data.prev_avg_reach ? Math.round((data.avg_reach - data.prev_avg_reach) / data.prev_avg_reach * 100) : null,
      icon: BarChart3, pastel: PASTEL.purple },
    { id: "clics", label: "Clics lien", value: fmtN(data.totals.clicks), delta: data.deltas.clicks,
      icon: MousePointerClick, pastel: PASTEL.blue },
    { id: "conversions", label: "Conversions", value: fmtN(data.totals.conversions), delta: data.deltas.conversions,
      icon: ShoppingCart, pastel: PASTEL.green },
  ].slice(0, 6);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const { data: reps } = await api.get(`/clients/${c.id}/reports`);
      let rep = reps?.[0];
      if (!rep) {
        const { data: gen } = await api.post(`/clients/${c.id}/reports/generate`, {});
        rep = gen;
        toast.success("Rapport généré — création du PDF visuel…");
      }
      // Page 1 = capture fidèle de l'écran Statistiques (vue d'ensemble)
      if (sub !== "overview") {
        setSub("overview");
        await new Promise((r) => setTimeout(r, 1200));
      }
      const node = document.querySelector('[data-testid="analyse-client-overview"]');
      if (node) {
        const image = await toPng(node, {
          pixelRatio: 2, backgroundColor: "#F6F7F9",
          width: 1920, style: { width: "1920px" },
        });
        const res = await api.post(`/reports/${rep.id}/visual-pdf`, { image }, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Rapport_${String(c.name).replace(/\s+/g, "_")}_${rep.month}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("PDF visuel généré — il sera joint si vous publiez ce rapport au client");
      } else {
        window.open(`${API}/api/reports/${rep.id}/pdf`, "_blank");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-3" data-testid="analyse-client">
      {/* Header client */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm px-4 py-3 flex flex-wrap items-center gap-3" data-testid="analyse-client-header">
        {c.logo
          ? <img src={c.logo} alt="" className="w-11 h-11 rounded-xl object-cover border border-neutral-100" />
          : <span className="w-11 h-11 rounded-xl flex items-center justify-center text-sm font-bold text-white" style={{ background: c.color || "#94A3B8" }}>{initials(c.name)}</span>}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-heading text-lg font-bold text-[#0F172A]">{c.name}</h2>
            <span className="flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-green-50 text-green-700 border border-green-200" data-testid="analyse-client-status">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Client actif
            </span>
          </div>
          <p className="text-[11px] text-neutral-400">Statistiques client</p>
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="analyse-client-tags">
          {[...new Set(tags)].slice(0, 4).map((t) => (
            <span key={t} className="text-[10px] border border-neutral-200 bg-neutral-50 rounded-full px-2 py-0.5 text-neutral-500">{t}</span>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {connected && (
            <>
          <button onClick={syncMeta} disabled={syncing} data-testid="analyse-sync-meta"
            className="inline-flex items-center gap-1.5 border border-[#E2E8F0] rounded-xl px-3.5 py-2 text-xs font-semibold text-[#0F172A] hover:bg-neutral-50 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Synchronisation…" : "Synchroniser"}
          </button>
          <button onClick={() => setSub("rapports")} data-testid="analyse-generate-report"
            className="inline-flex items-center gap-1.5 border border-[#E2E8F0] rounded-xl px-3.5 py-2 text-xs font-semibold text-[#0F172A] hover:bg-neutral-50 transition-colors">
            <Sparkles className="w-3.5 h-3.5" /> Générer un rapport
          </button>
          <button onClick={exportPdf} disabled={exporting} data-testid="analyse-client-export"
            className="inline-flex items-center gap-1.5 bg-[#0B132B] text-white rounded-xl px-4 py-2 text-xs font-semibold hover:bg-[#1C2A4F] transition-colors disabled:opacity-50">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Exporter
          </button>
            </>
          )}
        </div>
      </div>

      {!connected && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm px-6 py-12 flex flex-col items-center text-center gap-3" data-testid="analyse-client-no-meta">
          <span className="w-12 h-12 rounded-2xl bg-blue-50 text-[#1877F2] flex items-center justify-center">
            <Link2 className="w-5 h-5" />
          </span>
          <p className="font-heading font-bold text-sm text-[#0F172A]">Aucun compte Meta relié à {c.name}</p>
          <p className="text-[12px] text-neutral-500 max-w-md leading-snug">
            Les statistiques et les rapports reprennent exclusivement les chiffres réels du compte Meta
            (Instagram professionnel / Page Facebook) du client. La connexion et la synchronisation
            se font depuis la fiche client (onglet Community Management → Connexion Meta).
          </p>
          <button onClick={connectMeta} data-testid="connect-meta-btn"
            className="inline-flex items-center gap-1.5 bg-[#0B132B] text-white rounded-xl px-4 py-2 text-xs font-semibold hover:bg-[#1C2A4F] transition-colors">
            <Link2 className="w-3.5 h-3.5" /> Connecter Meta
          </button>
        </div>
      )}
      {connected && (
      <>
      {/* Sous-onglets */}
      <div className="flex gap-1 border-b border-neutral-200 overflow-x-auto" data-testid="analyse-client-tabs">
        {SUB_TABS.map(([id, l]) => (
          <button key={id} data-testid={`analyse-sub-${id}`} onClick={() => setSub(id)}
            className={`px-3.5 py-2 text-[13px] whitespace-nowrap border-b-2 -mb-px transition-colors ${
              sub === id ? "border-[#0B132B] font-semibold text-[#0F172A]" : "border-transparent text-neutral-400 hover:text-neutral-900"}`}>
            {l}
          </button>
        ))}
      </div>

      {sub === "overview" && (
        <div className="space-y-3" data-testid="analyse-client-overview">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="analyse-client-kpis">
            {kpis.map((k) => (
              <AnKpi key={k.id} testid={`analyse-ckpi-${k.id}`} icon={k.icon} pastel={k.pastel}
                value={k.value} label={k.label}
                delta={k.isPt ? null : k.delta}
                sub={k.isPt && k.delta != null ? `${k.delta >= 0 ? "+" : ""}${String(k.delta).replace(".", ",")} pt vs période préc.` : null} />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            <AnCard icon={Zap} title="Performances récentes" testid="analyse-client-perf" className="lg:col-span-5">
              <PerfChart series={data.perf_series || []} testid="analyse-client-perf-chart" />
            </AnCard>
            <AnCard icon={PieIcon} title="Répartition des contenus" testid="analyse-client-split" className="lg:col-span-3">
              {data.content_split?.length
                ? <Donut data={data.content_split} centerValue={fmtN(data.pubs_count)} centerLabel="publications" testid="analyse-client-donut" />
                : <p className="px-4 py-8 text-[13px] text-neutral-400">Aucune publication sur la période.</p>}
            </AnCard>
            <AnCard icon={BarChart3} title="Performances par réseau" testid="analyse-client-networks" className="lg:col-span-4">
              <NetworkTable rows={data.networks_table} testid="analyse-client-net" />
            </AnCard>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            <AnCard icon={FileText} title="Top publications" testid="analyse-client-top" className="lg:col-span-5">
              <TopPubs items={data.top_publications} testid="analyse-client-top-list" dense
                onOpen={(p) => navigate(`/publications/${p.id}`)} />
            </AnCard>
            <AnCard icon={Target} title="Objectifs du mois" testid="analyse-client-objectifs" className="lg:col-span-4"
              action={<button onClick={() => setSub("objectifs")} data-testid="analyse-client-obj-edit"
                className="ml-auto text-[11px] font-semibold text-[#0369A1] hover:underline">Définir</button>}>
              <ObjectifsBars objectives={data.objectives} testid="analyse-client-obj-list" />
            </AnCard>
            <AnCard icon={TrendingUp} title="Évolution des abonnés" testid="analyse-client-followers" className="lg:col-span-3">
              <FollowersChart followers={data.followers} testid="analyse-client-followers-chart" height={150} />
            </AnCard>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <AnCard icon={CalendarDays} title="Performance mensuelle" testid="analyse-client-monthly">
              <MonthlyTable rows={data.monthly} testid="analyse-client-monthly-table" />
            </AnCard>
            <AnCard icon={Lightbulb} title="Insights & recommandations DGA" testid="analyse-client-insights">
              <InsightsBlock insights={data.insights} testid="analyse-client-insights-list" />
            </AnCard>
          </div>
        </div>
      )}

      {sub === "contenus" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3" data-testid="analyse-client-contenus">
          <AnCard icon={PieIcon} title="Répartition des contenus" testid="analyse-cc-split" className="lg:col-span-4">
            {data.content_split?.length
              ? <Donut data={data.content_split} centerValue={fmtN(data.pubs_count)} centerLabel="publications" testid="analyse-cc-donut" height={200} />
              : <p className="px-4 py-8 text-[13px] text-neutral-400">Aucune publication sur la période.</p>}
          </AnCard>
          <AnCard icon={ImageIcon} title={`Top publications (${data.top_publications?.length || 0})`} testid="analyse-cc-top" className="lg:col-span-8">
            <TopPubs items={data.top_publications} testid="analyse-cc-top-list"
              onOpen={(p) => navigate(`/publications/${p.id}`)} />
          </AnCard>
        </div>
      )}

      {sub === "audiences" && (
        <div className="space-y-3" data-testid="analyse-client-audiences">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(data.followers || {}).map(([net, f], i) => (
              <AnKpi key={net} testid={`analyse-aud-${net}`} icon={Users}
                pastel={[PASTEL.rose, PASTEL.blue, PASTEL.purple, PASTEL.amber][i % 4]}
                value={fmtN(f.total)} label={`Abonnés ${NET_LABELS[net]}`} delta={f.delta} />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <AnCard icon={TrendingUp} title="Évolution des abonnés" testid="analyse-aud-chart">
              <FollowersChart followers={data.followers} testid="analyse-aud-followers" height={220} />
            </AnCard>
            <AnCard icon={CalendarDays} title="Abonnés par mois" testid="analyse-aud-monthly">
              <MonthlyTable rows={data.monthly} testid="analyse-aud-monthly-table" />
            </AnCard>
          </div>
        </div>
      )}

      {sub === "performances" && (
        <div className="space-y-3" data-testid="analyse-client-performances">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            <AnCard icon={Zap} title="Performances récentes" testid="analyse-perf-chart-card" className="lg:col-span-8">
              <PerfChart series={data.perf_series || []} testid="analyse-perf-chart" height={240} />
            </AnCard>
            <AnCard icon={BarChart3} title="Performances par réseau" testid="analyse-perf-networks" className="lg:col-span-4">
              <NetworkTable rows={data.networks_table} testid="analyse-perf-net" />
            </AnCard>
          </div>
          <AnCard icon={CalendarDays} title="Performance mensuelle" testid="analyse-perf-monthly">
            <MonthlyTable rows={data.monthly} testid="analyse-perf-monthly-table" />
          </AnCard>
        </div>
      )}

      {sub === "objectifs" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="analyse-client-objectifs-tab">
          <AnCard icon={Target} title="Objectifs du client" testid="analyse-obj-progress">
            <ObjectifsBars objectives={data.objectives} testid="analyse-obj-bars" />
          </AnCard>
          <AnCard icon={Target} title="Définir les objectifs" testid="analyse-obj-edit-card">
            <ClientObjectivesEditor clientId={c.id} objectives={data.objectives}
              onSaved={() => toast.success("Rechargez la période pour voir la progression à jour")} />
          </AnCard>
        </div>
      )}

      {sub === "insights" && (
        <div className="space-y-3" data-testid="analyse-client-insights-tab">
          <AnCard icon={Lightbulb} title="Insights & recommandations DGA" testid="analyse-insights-card"
            link={`/cm?tab=analyse&client=${c.id}&sub=rapports`} linkLabel="Générer le rapport complet">
            <InsightsBlock insights={data.insights} testid="analyse-insights-list" />
          </AnCard>
          <p className="text-[11px] text-neutral-400 px-1">
            Les insights sont calculés exclusivement à partir des données de la période — aucune observation n'est générée sans données suffisantes.
          </p>
        </div>
      )}

      {sub === "rapports" && (
        <div data-testid="analyse-client-rapports">
          <AnalyseRapports clientId={c.id} clients={clients} />
        </div>
      )}
      </>
      )}
    </div>
  );
}
